import { forwardRef, useEffect, useState, type InputHTMLAttributes } from 'react';
import { cx } from '@/lib/cx';
import { formatNumber, parseNumber } from '@/lib/format';

export interface NumberFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'type' | 'size'> {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  /** 単位（'円' '日' '隻' 等）。右側に表示 */
  unit?: string;
  /** 小数桁数（既定 0 = 整数） */
  decimals?: number;
  allowNegative?: boolean;
  invalid?: boolean;
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'full';
}

function toEditText(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? '' : String(v);
}

/**
 * 数値入力（右寄せ・3 桁カンマ）。
 * フォーカス中はカンマなしで編集し、離れるとカンマ区切りで表示する。全角数字・カンマ付き貼り付けも受け付ける。
 * react-hook-form では `<Controller render={({ field }) => <NumberField {...field} />} />` で使う。
 */
export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(function NumberField(
  { value, onChange, onFocus, onBlur, unit, decimals = 0, allowNegative = false, invalid, width = 'sm', className, disabled, ...rest },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => toEditText(value));

  // 外部から値が変わった場合（reset 等）に同期
  useEffect(() => {
    if (!focused) setText(toEditText(value));
  }, [value, focused]);

  const display = focused ? text : formatNumber(value ?? null, decimals);

  return (
    <span className={cx('bs-affix bs-number', `bs-w-${width}`, invalid && 'is-invalid', disabled && 'is-disabled', className)}>
      <input
        ref={ref}
        type="text"
        inputMode={decimals > 0 ? 'decimal' : 'numeric'}
        autoComplete="off"
        className="bs-input bs-number__input"
        aria-invalid={invalid || undefined}
        disabled={disabled}
        value={display}
        onFocus={(e) => {
          setFocused(true);
          setText(toEditText(value));
          onFocus?.(e);
        }}
        onChange={(e) => {
          // 負数不可なら符号は入力段階で取り除く
          const raw = allowNegative ? e.target.value : e.target.value.replace(/[-－ー−]/g, '');
          setText(raw);
          let n = parseNumber(raw);
          if (n != null) {
            const factor = 10 ** decimals;
            n = Math.round(n * factor) / factor;
          }
          onChange(n);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        {...rest}
      />
      {unit && <span className="bs-affix__item bs-affix__unit">{unit}</span>}
    </span>
  );
});
